<template>
  <div class="experience-library">
    <section class="experience-library__section">
      <header class="experience-library__header experience-family__header">
        <p class="experience-library__intro">
          Build and manage reusable quiz and survey experiences here, then attach them to campaigns.
        </p>
        <div class="experience-library__header-actions">
          <router-link class="experience-library__cta" :to="{ path: '/dashboard/experiences/new' }">
            Create new experience
          </router-link>
        </div>
      </header>

      <div v-if="libraryMessage" class="experience-library__banner" :class="`experience-library__banner--${libraryMessage.kind}`">
        {{ libraryMessage.text }}
      </div>

      <div class="experience-library__section-head">
        <h2>Experience families</h2>
        <span class="experience-library__count">{{ familyGroups.length }}</span>
      </div>

      <div v-if="familyGroups.length === 0" class="experience-library__empty">
        No experiences yet. Create a draft or publish an experience to start building reusable families.
      </div>

      <div v-else class="experience-family-list">
        <article v-for="family in familyGroups" :key="family.familyKey" class="experience-family" :class="{ 'experience-family--collapsed': !isFamilyExpanded(family.familyKey) }">
          <header class="experience-family__header">
            <div>
              <button
                type="button"
                class="experience-family__toggle"
                :aria-expanded="isFamilyExpanded(family.familyKey)"
                @click="toggleFamily(family.familyKey)"
              >
                <span class="experience-family__chevron" :class="{ 'experience-family__chevron--open': isFamilyExpanded(family.familyKey) }">▸</span>
                <h3>{{ family.displayName }}</h3>
              </button>
              <p class="experience-family__mode">{{ modeLabel(family.experienceMode) }}</p>
              <p class="experience-family__summary">
                {{ family.templateFamilyId || 'General template' }} •
                {{ family.totalRecords }} record{{ family.totalRecords === 1 ? '' : 's' }} •
                {{ family.activeDrafts.length }} draft{{ family.activeDrafts.length === 1 ? '' : 's' }} •
                {{ family.latestPublished ? `${exactUsageLabel(family.latestPublished)} attached` : 'No published attachment yet' }}
              </p>
            </div>
            <div class="experience-family__header-right">
              <div class="experience-family__badges">
                <span class="experience-card__version-state experience-card__version-state--latest">
                  {{ family.latestPublished ? `v${family.latestPublished.versionNumber || 1} live` : 'draft only' }}
                </span>
                <span class="experience-library__count">{{ family.totalRecords }}</span>
              </div>
              <div class="experience-family__primary-actions">
                <router-link
                  v-if="family.activeDrafts.length > 0"
                  class="experience-family__action-btn experience-family__action-btn--primary"
                  :to="{ path: `/dashboard/experiences/${family.activeDrafts[0].localKey}` }"
                >
                  Edit latest draft
                </router-link>
                <button
                  v-if="family.activeDrafts.length > 0 || family.latestPublished"
                  type="button"
                  class="experience-family__action-btn experience-family__action-btn--primary"
                  @click="duplicateRecord((family.activeDrafts[0] ?? family.latestPublished)!.localKey)"
                >
                  Duplicate
                </button>
              </div>
            </div>
          </header>

          <div v-if="isFamilyExpanded(family.familyKey)" class="experience-family__body">
          <div v-if="family.latestPublished" class="experience-family__block">
            <div class="experience-family__block-head">
              <h4>Latest published</h4>
              <span class="experience-card__status experience-card__status--published">published</span>
            </div>
            <article class="experience-card experience-card--nested">
              <div class="experience-card__header">
                <div>
                  <h5>{{ family.latestPublished.name }}</h5>
                </div>
                <div class="experience-card__badges">
                  <span class="experience-card__version-state experience-card__version-state--latest">latest</span>
                </div>
              </div>

              <div class="experience-card__meta">
                <span>{{ family.latestPublished.templateFamilyId || 'General template' }}</span>
                <span>•</span>
                <span>{{ family.latestPublished.rounds }} rounds</span>
                <span>•</span>
                <span>{{ family.latestPublished.stages }} stages</span>
                <span>•</span>
                <span>{{ family.latestPublished.questions }} questions</span>
              </div>

              <div class="experience-card__details">
                <div>
                  <dt>Version</dt>
                  <dd>v{{ family.latestPublished.versionNumber || 1 }}</dd>
                </div>
                <div>
                  <dt>Updated</dt>
                  <dd>{{ formatDate(family.latestPublished.updatedAt) }}</dd>
                </div>
                <div>
                  <dt>Campaign usage</dt>
                  <dd>{{ exactUsageLabel(family.latestPublished) }}</dd>
                </div>
                <div>
                  <dt>Key</dt>
                  <dd>{{ family.latestPublished.localKey }}</dd>
                </div>
              </div>

              <div v-if="family.latestPublished.usage?.campaigns?.length" class="experience-card__usage-preview experience-card__usage-chips">
                <span class="experience-card__usage-chips-label">Pinned by</span>
                <router-link
                  v-for="campaign in family.latestPublished.usage!.campaigns"
                  :key="campaign.campaignId"
                  class="experience-card__campaign-chip"
                  :to="{ path: `/dashboard/campaigns/${campaign.campaignId}/edit` }"
                >
                  {{ campaign.campaignName }}
                </router-link>
              </div>

              <div class="experience-card__actions">
                <router-link class="experience-card__button experience-card__button--secondary" :to="{ path: `/dashboard/experiences/${family.latestPublished.localKey}` }">
                  Inspect version
                </router-link>
                <button
                  v-if="family.latestPublished.lineageId"
                  type="button"
                  class="experience-card__button experience-card__button--secondary"
                  :disabled="exportStates[family.latestPublished.lineageId]?.loading"
                  @click="handleDownloadCsv(family.latestPublished.lineageId)"
                >
                  {{ exportStates[family.latestPublished.lineageId]?.loading ? 'Fetching…' : 'Download CSV' }}
                </button>
                <button
                  v-if="family.latestPublished.lineageId"
                  type="button"
                  class="experience-card__button experience-card__button--data"
                  :disabled="exportStates[family.latestPublished.lineageId]?.loading"
                  @click="handleOpenVisualizer(family.latestPublished.lineageId)"
                >
                  Visualize data
                </button>
                <button type="button" class="experience-card__button experience-card__button--danger" @click="archiveRecord(family.latestPublished.localKey)">
                  Archive
                </button>
              </div>
            </article>
          </div>

          <div v-if="family.activeDrafts.length > 0" class="experience-family__block">
            <div class="experience-family__block-head">
              <h4>Drafts</h4>
              <span class="experience-library__count">{{ family.activeDrafts.length }}</span>
            </div>
            <div class="experience-family__nested-grid">
              <article v-for="record in family.activeDrafts" :key="record.localKey" class="experience-card experience-card--nested">
                <div class="experience-card__header">
                  <div>
                    <h5>{{ record.name }}</h5>
                  </div>
                  <span class="experience-card__status experience-card__status--draft">draft</span>
                </div>

                <div class="experience-card__meta">
                  <span>{{ record.templateFamilyId || 'General template' }}</span>
                  <span>•</span>
                  <span>{{ record.rounds }} rounds</span>
                  <span>•</span>
                  <span>{{ record.stages }} stages</span>
                  <span>•</span>
                  <span>{{ record.questions }} questions</span>
                </div>

                <div class="experience-card__details">
                  <div>
                    <dt>Version</dt>
                    <dd>v{{ record.versionNumber || 1 }}</dd>
                  </div>
                  <div>
                    <dt>Updated</dt>
                    <dd>{{ formatDate(record.updatedAt) }}</dd>
                  </div>
                  <div>
                    <dt>Campaign</dt>
                    <dd>{{ record.campaignName || 'Untitled campaign' }}</dd>
                  </div>
                  <div>
                    <dt>Key</dt>
                    <dd>{{ record.localKey }}</dd>
                  </div>
                </div>

                <div class="experience-card__actions">
                  <router-link class="experience-card__button experience-card__button--secondary" :to="{ path: `/dashboard/experiences/${record.localKey}` }">
                    Edit draft
                  </router-link>
                  <router-link class="experience-card__button" :to="{ path: '/', query: { experienceDraft: record.localKey, prefill: 'true' } }">
                    Open in builder
                  </router-link>
                  <button type="button" class="experience-card__button experience-card__button--secondary" @click="duplicateRecord(record.localKey)">
                    Duplicate
                  </button>
                  <button type="button" class="experience-card__button experience-card__button--danger" @click="archiveRecord(record.localKey)">
                    Archive
                  </button>
                </div>
              </article>
            </div>
          </div>

          <div v-if="family.olderPublished.length > 0" class="experience-family__block">
            <div class="experience-family__block-head">
              <h4>Older published versions</h4>
              <span class="experience-library__count">{{ family.olderPublished.length }}</span>
            </div>
            <div class="experience-family__nested-grid">
              <article v-for="record in family.olderPublished" :key="record.localKey" class="experience-card experience-card--nested experience-card--older">
                <div class="experience-card__header">
                  <div>
                    <h5>{{ record.name }}</h5>
                  </div>
                  <div class="experience-card__badges">
                    <span class="experience-card__status experience-card__status--published">published</span>
                    <span
                      class="experience-card__version-state"
                      :class="{ 'experience-card__version-state--original': family.originalPublished?.localKey === record.localKey }"
                    >
                      {{ family.originalPublished?.localKey === record.localKey ? 'original' : 'older' }}
                    </span>
                  </div>
                </div>

                <div class="experience-card__meta">
                  <span>{{ record.templateFamilyId || 'General template' }}</span>
                  <span>•</span>
                  <span>{{ record.rounds }} rounds</span>
                  <span>•</span>
                  <span>{{ record.stages }} stages</span>
                  <span>•</span>
                  <span>{{ record.questions }} questions</span>
                </div>

                <div class="experience-card__details">
                  <div>
                    <dt>Version</dt>
                    <dd>v{{ record.versionNumber || 1 }}</dd>
                  </div>
                  <div>
                    <dt>Superseded by</dt>
                    <dd>{{ latestVersionLabel(record) }}</dd>
                  </div>
                  <div>
                    <dt>Campaign usage</dt>
                    <dd>{{ exactUsageLabel(record) }}</dd>
                  </div>
                  <div>
                    <dt>Key</dt>
                    <dd>{{ record.localKey }}</dd>
                  </div>
                </div>

                <div v-if="record.usage?.campaigns?.length" class="experience-card__usage-preview experience-card__usage-chips">
                  <span class="experience-card__usage-chips-label">Still pinned by</span>
                  <router-link
                    v-for="campaign in record.usage!.campaigns"
                    :key="campaign.campaignId"
                    class="experience-card__campaign-chip"
                    :to="{ path: `/dashboard/campaigns/${campaign.campaignId}/edit` }"
                  >
                    {{ campaign.campaignName }}
                  </router-link>
                </div>

                <div class="experience-card__actions">
                  <router-link class="experience-card__button experience-card__button--secondary" :to="{ path: `/dashboard/experiences/${record.localKey}` }">
                    Inspect version
                  </router-link>
                  <router-link class="experience-card__button" :to="{ path: '/', query: { experienceDraft: record.localKey, prefill: 'true' } }">
                    Open in builder
                  </router-link>
                  <button type="button" class="experience-card__button experience-card__button--secondary" @click="duplicateRecord(record.localKey)">
                    Duplicate
                  </button>
                  <button type="button" class="experience-card__button experience-card__button--danger" @click="archiveRecord(record.localKey)">
                    Archive
                  </button>
                </div>
              </article>
            </div>
          </div>
          </div>
        </article>
      </div>
    </section>

    <section v-if="archivedRecords.length > 0" class="experience-library__section experience-library__section--archived">
      <div class="experience-library__section-head">
        <button
          type="button"
          class="experience-family__toggle"
          :aria-expanded="archivedExpanded"
          @click="toggleArchived"
        >
          <span class="experience-family__chevron" :class="{ 'experience-family__chevron--open': archivedExpanded }">▸</span>
          <h2>Archived</h2>
        </button>
        <span class="experience-library__count">{{ archivedRecords.length }}</span>
      </div>

      <div v-if="archivedExpanded" class="experience-library__archived-list">
        <article v-for="record in archivedRecords" :key="record.localKey" class="experience-archived-row">
          <div class="experience-archived-row__main">
            <p class="experience-card__mode">{{ modeLabel(record.experienceMode) }}</p>
            <h3 class="experience-archived-row__name">{{ record.name }}</h3>
            <p class="experience-archived-row__meta">
              v{{ record.versionNumber || 1 }} • {{ versionCountLabel(record) }} • Archived {{ formatDate(record.archivedAt || record.updatedAt) }} • {{ record.localKey }}
            </p>
          </div>
          <div class="experience-family__primary-actions">
            <button
              type="button"
              class="experience-family__action-btn experience-family__action-btn--primary"
              @click="restoreRecord(record.localKey)"
            >
              Restore
            </button>
            <button
              type="button"
              class="experience-family__action-btn experience-family__action-btn--danger"
              @click="permanentlyDeleteRecord(record.localKey)"
            >
              Delete
            </button>
          </div>
        </article>
      </div>
    </section>
  </div>

  <ExperienceDataVisualizer
    v-if="activeVisualizer.open && activeVisualizer.data"
    :data="activeVisualizer.data"
    @close="activeVisualizer.open = false"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { experiencesService } from '@/services/experiences'
import ExperienceDataVisualizer from '@/components/ExperienceDataVisualizer.vue'
import type { ExperienceExportResponse } from '@/types/experience-export'
import { answerRowsToCsv } from '@/utils/json-to-csv'
import {
  archiveExperienceLibraryRecord,
  duplicateExperienceLibraryRecord,
  listExperienceLibraryRecords,
  permanentlyDeleteExperienceLibraryRecord,
  restoreExperienceLibraryRecord,
  type ExperienceLibraryRecord,
} from '@/utils/experienceLibrary'

const router = useRouter()
const records = ref<ExperienceLibraryRecord[]>([])
const archivedRecords = ref<ExperienceLibraryRecord[]>([])
const expandedFamilies = ref<Set<string>>(new Set())
function isFamilyExpanded(key: string) {
  return expandedFamilies.value.has(key)
}
function toggleFamily(key: string) {
  const next = new Set(expandedFamilies.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  expandedFamilies.value = next
}
const archivedExpanded = ref(false)
function toggleArchived() {
  archivedExpanded.value = !archivedExpanded.value
}
const latestPublishedRecords = ref<ExperienceLibraryRecord[]>([])
const olderPublishedRecords = ref<ExperienceLibraryRecord[]>([])
const draftRecords = ref<ExperienceLibraryRecord[]>([])
const libraryMessage = ref<{ kind: 'error' | 'success'; text: string } | null>(null)

interface ExperienceFamilyGroup {
  familyKey: string
  displayName: string
  experienceMode: ExperienceLibraryRecord['experienceMode']
  templateFamilyId?: string
  latestPublished: ExperienceLibraryRecord | null
  originalPublished: ExperienceLibraryRecord | null
  olderPublished: ExperienceLibraryRecord[]
  activeDrafts: ExperienceLibraryRecord[]
  totalRecords: number
  updatedAt: string
}

function lineageKey(record: ExperienceLibraryRecord): string {
  return record.lineageId || record.quizConfig.experienceDefinitionId || record.localKey
}

function versionCountLabel(record: ExperienceLibraryRecord): string {
  const count = listExperienceLibraryRecords({ includeArchived: true }).filter((item) => lineageKey(item) === lineageKey(record)).length
  return `${count} version${count === 1 ? '' : 's'}`
}

function latestPublishedByLineage(records: ExperienceLibraryRecord[]): Map<string, ExperienceLibraryRecord> {
  const latest = new Map<string, ExperienceLibraryRecord>()
  for (const record of records) {
    const key = lineageKey(record)
    const existing = latest.get(key)
    if (!existing) {
      latest.set(key, record)
      continue
    }
    const recordVersion = record.versionNumber || 1
    const existingVersion = existing.versionNumber || 1
    if (recordVersion > existingVersion || (recordVersion === existingVersion && new Date(record.updatedAt).getTime() > new Date(existing.updatedAt).getTime())) {
      latest.set(key, record)
    }
  }
  return latest
}

function latestVersionLabel(record: ExperienceLibraryRecord): string {
  const latest = latestPublishedByLineage(listExperienceLibraryRecords({ includeArchived: true }).filter((item) => item.status === 'published' && !item.archivedAt)).get(lineageKey(record))
  return latest ? `v${latest.versionNumber || 1}` : 'Latest'
}

const familyGroups = computed<ExperienceFamilyGroup[]>(() => {
  const groups = new Map<string, ExperienceLibraryRecord[]>()
  for (const record of records.value) {
    const key = lineageKey(record)
    const existing = groups.get(key) || []
    existing.push(record)
    groups.set(key, existing)
  }

  return [...groups.entries()]
    .map(([familyKey, items]) => {
      const published = items
        .filter((item) => item.status === 'published')
        .sort((a, b) => (b.versionNumber || 1) - (a.versionNumber || 1) || new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        const drafts = items
          .filter((item) => item.status === 'draft')
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        const latestPublished = published[0] || null
        const originalPublished = published[published.length - 1] || null
        const representative = latestPublished || drafts[0] || items[0]
        const updatedAt = [latestPublished, drafts[0], items[0]]
          .filter(Boolean)
          .map((item) => item!.updatedAt)
          .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || ''

      return {
        familyKey,
          displayName: representative?.name || 'Untitled Experience',
          experienceMode: representative?.experienceMode || 'quiz',
          templateFamilyId: representative?.templateFamilyId,
          latestPublished,
          originalPublished,
          olderPublished: latestPublished ? published.slice(1) : published,
          activeDrafts: drafts,
          totalRecords: items.length,
          updatedAt,
      }
    })
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
})

function exactUsageLabel(record: ExperienceLibraryRecord): string {
  const count = record.usage?.exactVersionCount || 0
  if (count === 0) return 'Not attached'
  return `${count} campaign${count === 1 ? '' : 's'}`
}

function campaignPreviewLabel(record: ExperienceLibraryRecord): string {
  const campaigns = record.usage?.campaigns || []
  if (campaigns.length === 0) return 'no campaigns'
  if (campaigns.length === 1) return campaigns[0].campaignName
  if (campaigns.length === 2) return `${campaigns[0].campaignName} and ${campaigns[1].campaignName}`
  return `${campaigns[0].campaignName}, ${campaigns[1].campaignName}, and ${campaigns.length - 2} more`
}

async function refreshRecords() {
  await experiencesService.syncLibrary(true)
  records.value = listExperienceLibraryRecords()
  const published = records.value.filter((record) => record.status === 'published')
  const latestByLineage = latestPublishedByLineage(published)
  latestPublishedRecords.value = published.filter((record) => latestByLineage.get(lineageKey(record))?.localKey === record.localKey)
  olderPublishedRecords.value = published.filter((record) => latestByLineage.get(lineageKey(record))?.localKey !== record.localKey)
  draftRecords.value = records.value.filter((record) => record.status === 'draft')
  archivedRecords.value = listExperienceLibraryRecords({ includeArchived: true }).filter((record) => Boolean(record.archivedAt))
}

function setLibraryMessage(kind: 'error' | 'success', text: string) {
  libraryMessage.value = { kind, text }
}

function formatDate(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

function modeLabel(mode: ExperienceLibraryRecord['experienceMode']): string {
  if (mode === 'survey') return 'Survey'
  if (mode === 'all-in-one') return 'All-In-One'
  return 'Quiz'
}

async function archiveRecord(localKey: string) {
  const target = records.value.find((record) => record.localKey === localKey)
  const shouldArchive = typeof window === 'undefined'
    ? true
    : window.confirm(`Archive "${target?.name || 'this experience'}"? You can restore it later.`)
  if (!shouldArchive) return
  archiveExperienceLibraryRecord(localKey)
  await experiencesService.setArchived(localKey, true)
  setLibraryMessage('success', 'Experience archived.')
  await refreshRecords()
}

async function restoreRecord(localKey: string) {
  restoreExperienceLibraryRecord(localKey)
  await experiencesService.setArchived(localKey, false)
  setLibraryMessage('success', 'Experience restored.')
  await refreshRecords()
}

async function permanentlyDeleteRecord(localKey: string) {
  const target = archivedRecords.value.find((record) => record.localKey === localKey)
  const shouldDelete = typeof window === 'undefined'
    ? true
    : window.confirm(`Permanently delete "${target?.name || 'this archived experience'}"? This cannot be undone.`)
  if (!shouldDelete) return
  try {
    const deletedRemotely = await experiencesService.deleteExperience(localKey)
    permanentlyDeleteExperienceLibraryRecord(localKey)
    await refreshRecords()
    setLibraryMessage('success', deletedRemotely
      ? 'Experience deleted permanently.'
      : 'Experience deleted from this browser.'
    )
  } catch (error) {
    console.warn('Failed to permanently delete experience', error)
    setLibraryMessage('error', 'Unable to delete this archived experience right now. Please try again.')
  }
}

async function duplicateRecord(localKey: string) {
  const duplicated = duplicateExperienceLibraryRecord(localKey)
  if (duplicated) {
    await experiencesService.saveExperience(duplicated)
    await refreshRecords()
    router.push({ path: `/dashboard/experiences/${duplicated.localKey}` })
  }
}

// ─── Per-family data export ───────────────────────────────────────────────────

type ExportState = { loading: boolean; error: string | null; data: ExperienceExportResponse | null }
const exportStates = reactive<Record<string, ExportState>>({})

const activeVisualizer = reactive<{ open: boolean; data: ExperienceExportResponse | null }>({
  open: false,
  data: null,
})

function ensureExportState(lineageId: string): ExportState {
  if (!exportStates[lineageId]) {
    exportStates[lineageId] = { loading: false, error: null, data: null }
  }
  return exportStates[lineageId]!
}

async function fetchFamilyExport(lineageId: string): Promise<ExperienceExportResponse | null> {
  const state = ensureExportState(lineageId)
  if (state.data) return state.data
  state.loading = true
  state.error = null
  try {
    const data = await experiencesService.getExperienceExport(lineageId)
    state.data = data
    return data
  } catch (err: unknown) {
    const message =
      err && typeof err === 'object' && 'response' in err
        ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to fetch export data.')
        : 'Failed to fetch export data.'
    state.error = message
    return null
  } finally {
    state.loading = false
  }
}

async function handleDownloadCsv(lineageId: string) {
  const data = await fetchFamilyExport(lineageId)
  if (!data) return

  const sessionsLookup = new Map(data.sessions.map((s) => [s.sessionId, s]))
  const campaignsLookup = new Map(data.campaigns.map((c) => [c.campaignId, c]))
  const csv = answerRowsToCsv(data.answers, sessionsLookup, campaignsLookup)
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${data.experience.name.replace(/[^a-zA-Z0-9-_]/g, '_')}-answers-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

async function handleOpenVisualizer(lineageId: string) {
  const data = await fetchFamilyExport(lineageId)
  if (!data) return
  activeVisualizer.data = data
  activeVisualizer.open = true
}

onMounted(() => {
  refreshRecords()
})
</script>

<style scoped>
.experience-library {
  min-height: 100vh;
  max-width: 88rem;
  padding: 1.5rem;
  margin: 6.25rem auto 3.125rem;
  background-color: var(--ui-inverse-surface);
  color: var(--vt-c-white);
  border-radius: 1rem;
  box-shadow:
    0 0.625rem 1.875rem rgba(0, 0, 0, 0.3),
    0 0 0 0.0625rem rgba(var(--vt-c-accent-rgb), 0.1);
  backdrop-filter: blur(0.625rem);
}

.experience-library__header {
  width: 100%;
  margin: 0 0 1rem;
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
}

.experience-library__eyebrow {
  margin: 0 0 0.35rem;
  color: rgba(var(--vt-c-accent-rgb), 0.8);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.experience-library__header h1 {
  margin: 0;
  font-size: clamp(2rem, 4vw, 3.1rem);
}

.experience-library__intro {
  max-width: 42rem;
  margin: 0.8rem 0 0;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.55;
}

.experience-library__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 1rem;
  border-radius: 999px;
  border: 1px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.experience-library__header-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.experience-library__cta--ghost {
  border-color: rgba(var(--vt-c-accent-rgb), 0.32);
  background: var(--ui-inverse-surface);
  color: var(--vt-c-white);
}

.experience-library__section {
  width: 100%;
  margin: 0;
  padding: 1.1rem;
  border-radius: 1.2rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.16);
  background: var(--ui-inverse-surface);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.18);
}

.experience-library__banner {
  margin: 0 0 1rem;
  padding: 0.9rem 1rem;
  border-radius: 0.95rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.16);
  font-size: 0.92rem;
  line-height: 1.45;
  background: var(--ui-inverse-surface);
}

.experience-library__banner--success {
  background: rgba(var(--vt-c-accent-rgb), 0.08);
  border-color: rgba(var(--vt-c-accent-rgb), 0.24);
  color: #cffff0;
}

.experience-library__banner--error {
  background: rgba(255, 91, 113, 0.08);
  border-color: rgba(255, 91, 113, 0.24);
  color: #ffd7dc;
}

.experience-library__section--archived {
  margin-top: 1rem;
}

.experience-library__section-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.experience-library__section-head h2 {
  margin: 0;
  font-size: 1.2rem;
}

.experience-library__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-size: 0.78rem;
  font-weight: 700;
}

.experience-library__empty {
  padding: 1.2rem 1.3rem;
  border-radius: 1rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.18);
  background: var(--ui-inverse-surface);
  color: rgba(255, 255, 255, 0.72);
}

.experience-library__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
  gap: 1rem;
}

.experience-family-list {
  display: grid;
  gap: 0.85rem;
}

.experience-family {
  padding: 1.1rem;
  border-radius: 1.2rem;
  border: 2px solid var(--vt-c-accent);
  background: rgba(15, 23, 42, 0.42);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.18);
}

.experience-family__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.experience-family__header > div:first-child {
  display: grid;
  justify-items: start;
  text-align: left;
  flex: 1;
}

.experience-family__mode {
  margin: 0 0 0.25rem;
  color: rgba(var(--vt-c-accent-rgb), 0.82);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.experience-family__header h3 {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.15;
}

.experience-family__header .experience-family__toggle,
.experience-library__section-head .experience-family__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  width: auto;
  min-height: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: inherit;
  font: inherit;
  text-align: left;
}

.experience-family__header .experience-family__toggle:focus-visible,
.experience-library__section-head .experience-family__toggle:focus-visible {
  outline: 2px solid rgba(var(--vt-c-accent-rgb), 0.45);
  outline-offset: 2px;
  border-radius: 0.4rem;
}

.experience-family__toggle:hover h3 {
  color: var(--vt-c-accent);
}

.experience-family__chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  color: var(--vt-c-accent);
  font-size: 2rem;
  line-height: 1;
  transition: transform 150ms ease;
  animation: experience-family-chevron-pulse 1.6s ease-in-out infinite;
  transform-origin: center;
}

.experience-family__chevron--open {
  animation: none;
  transform: rotate(90deg);
  opacity: 1;
}

@keyframes experience-family-chevron-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.25);
    opacity: 1;
  }
}

.experience-family__body {
  margin-top: 1rem;
}

.experience-family__summary {
  margin: 0.45rem 0 0;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.45;
  font-size: 0.9rem;
}

.experience-family__header-right {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  align-items: flex-end;
  flex-shrink: 0;
}

.experience-family__badges {
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
  align-items: center;
}

.experience-family__primary-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
}

.experience-family__primary-actions > * {
  flex: 0 0 auto;
}

.experience-family__action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: auto;
  min-height: 0;
  min-width: 7.5rem;
  padding: 0.55rem 1rem;
  margin: 0;
  border: 1px solid transparent;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.1;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  appearance: none;
  box-shadow: none;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.experience-family__action-btn:hover {
  opacity: 0.88;
}

.experience-family__action-btn--primary {
  background: var(--vt-c-accent);
  border-color: var(--vt-c-accent);
  color: var(--color-background);
}

.experience-family__action-btn--secondary {
  background: rgba(var(--vt-c-accent-rgb), 0.1);
  border-color: rgba(var(--vt-c-accent-rgb), 0.32);
  color: var(--vt-c-accent);
}

.experience-family__action-btn--secondary:hover {
  background: rgba(var(--vt-c-accent-rgb), 0.2);
  border-color: var(--vt-c-accent);
  opacity: 1;
}

.experience-family__action-btn--danger {
  background: #ff7070;
  border-color: #ff7070;
  color: var(--color-background);
}

.experience-family__action-btn--danger:hover {
  opacity: 0.88;
}

.experience-library__archived-list {
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
}

.experience-archived-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 1rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.16);
  background: rgba(255, 255, 255, 0.03);
}

.experience-archived-row__main {
  display: grid;
  justify-items: start;
  text-align: left;
  flex: 1;
  min-width: 0;
}

.experience-archived-row__name {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.15;
}

.experience-archived-row__meta {
  margin: 0.45rem 0 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.84rem;
}

.experience-family__block {
  margin-top: 1rem;
  display: grid;
  gap: 0.75rem;
}

.experience-family__block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.experience-family__block-head h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.01em;
}

.experience-family__nested-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
  gap: 0.85rem;
}

.experience-card {
  padding: 1rem;
  border-radius: 1.1rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.15);
  background: rgba(15, 23, 42, 0.72);
  box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.18);
}

.experience-card__usage-preview {
  margin: 0.85rem 0 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.85rem;
  line-height: 1.45;
}

.experience-card__usage-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
}

.experience-card__usage-chips-label {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.8rem;
}

.experience-card__campaign-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  border-radius: 999px;
  background: rgba(var(--vt-c-accent-rgb), 0.15);
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.32);
  color: var(--vt-c-accent);
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
}

.experience-card__campaign-chip:hover {
  background: rgba(var(--vt-c-accent-rgb), 0.28);
}

.experience-card__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.experience-card__header > div:first-child {
  display: grid;
  gap: 0.4rem;
  min-width: 0;
}

.experience-card__badges {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.experience-card__mode {
  margin: 0 0 0.25rem;
  color: rgba(var(--vt-c-accent-rgb), 0.82);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.experience-card__header h3 {
  margin: 0;
  font-size: 1.05rem;
}

.experience-card__status {
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.experience-card__status--published {
  background: #22c55e;
  color: #062814;
}

.experience-card__status--draft {
  background: #facc15;
  color: #422006;
}

.experience-card__status--archived {
  background: #94a3b8;
  color: #0f172a;
}

.experience-card__version-state {
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
}

.experience-card__version-state--latest {
  background: var(--vt-c-accent);
  color: var(--color-background);
}

.experience-card__version-state--original {
  background: rgba(var(--vt-c-accent-rgb), 0.18);
  border: 1px solid var(--vt-c-accent);
  color: var(--vt-c-accent);
}

.experience-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.8rem;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.8rem;
}

.experience-card__meta--header {
  margin-top: 0;
}

.experience-card--nested > .experience-card__meta {
  display: none;
}

.experience-card__details {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-top: 1rem;
}

.experience-card__details dt {
  margin: 0 0 0.2rem;
  color: rgba(255, 255, 255, 0.48);
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
}

.experience-card__details dd {
  margin: 0;
  color: var(--vt-c-white);
  font-size: 0.82rem;
  line-height: 1.35;
  word-break: break-word;
}

.experience-card__actions {
  margin-top: 0.85rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(5.5rem, 1fr));
  gap: 0.5rem;
}

.experience-card__button {
  border: 1px solid var(--vt-c-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 2.5rem;
  padding: 0.55rem 0.75rem;
  margin: 0;
  box-sizing: border-box;
  border-radius: 0.75rem;
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.1;
  text-decoration: none;
  cursor: pointer;
  text-align: center;
  appearance: none;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}

.experience-card__button:hover {
  opacity: 0.88;
}

.experience-card__button--secondary {
  background: var(--vt-c-accent);
  border-color: var(--vt-c-accent);
  color: var(--color-background);
}

.experience-card__button--secondary:hover {
  opacity: 0.88;
}

.experience-card__button--danger {
  background: #ff7070;
  border-color: #ff7070;
  color: var(--color-background);
}

.experience-card__button--danger:hover {
  opacity: 0.88;
}

.experience-card__button--data {
  background: #b490ff;
  border-color: #b490ff;
  color: var(--color-background);
}

.experience-card__button--data:hover {
  opacity: 0.88;
}

.experience-card__button:disabled {
  opacity: 0.48;
  cursor: not-allowed;
  pointer-events: none;
}

.experience-card--archived {
  opacity: 0.82;
}

.experience-card--older {
  opacity: 0.9;
}

.experience-card--nested h5 {
  margin: 0;
  font-size: 0.98rem;
}

.experience-card--nested .experience-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  text-align: left;
}

.experience-card--nested .experience-card__meta {
  justify-content: flex-start;
  text-align: left;
}

.experience-card--nested .experience-card__details {
  text-align: left;
}

@media (max-width: 767px) {
  .experience-library__header {
    flex-direction: column;
  }

  .experience-family__header {
    flex-direction: column;
  }

  .experience-family__header-right {
    align-items: flex-start;
    width: 100%;
  }

  .experience-family__primary-actions {
    justify-content: flex-start;
    width: 100%;
  }

  .experience-card__details {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .experience-card__actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
